import * as Yup from "yup";
import { Formik } from "formik";
import { CustomCombobox, Dropzone } from "@/components";
import { Button, buttonVariants } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { showSuccessToast } from "@/lib/toast";
import { addBulkStudents } from "@/api/students";
import { useMainLayoutContext } from "@/context/MainLayoutContext";
import { Loader2Icon } from "lucide-react";

type AddMultipleStudentProps = { onClose?: () => void };

const AddMultipleStudent = ({ onClose }: AddMultipleStudentProps) => {
  const queryClient = useQueryClient();
  const { classes } = useMainLayoutContext();

  const initialValues = {
    classId: "",
    students: null,
  };

  const validationSchema = Yup.object().shape({
    classId: Yup.string().required("This field is required"),
    students: Yup.mixed()
      .required("File is required")
      .test("students", "Only Excel files are allowed", (value) => {
        if (value) {
          const file = value as File;
          const supportedFormats = ["xls", "xlsx"];

          return supportedFormats.includes(
            file?.name?.split(".").pop()!.toLowerCase(),
          );
        }
        return true;
      }),
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: addBulkStudents,
    onSuccess: () => {
      showSuccessToast("Students added successfully");

      queryClient.invalidateQueries({ queryKey: ["students"] });
      onClose?.();
    },
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={values => mutate(values)}
      validationSchema={validationSchema}
    >
      {({ values, handleSubmit, setFieldValue }) => (
        <form
          className="flex flex-col justify-between gap-4 pb-8"
          onSubmit={handleSubmit}
        >
          <Link
            to="/sample-students.xlsx"
            target="_blank"
            download
            className="text-sm font-semibold w-fit text-primary"
          >
            Download Sample Excel File
          </Link>
          <CustomCombobox
            options={classes?.map((classItem) => ({
              value: classItem.id,
              label: classItem.class,
            })) || []}
            value={values.classId}
            name="classId"
            onChange={(val) => setFieldValue("classId", val)}
            placeholder="Select Class"
            label="Class"
          />
          <Dropzone name="students" />

          <div className="flex items-center justify-end w-full gap-2">
            <DialogClose
              type="button"
              className={buttonVariants({
                variant: "outline",
                className: "h-8.5",
              })}
            >
              Cancel
            </DialogClose>
            <Button type="submit" className="h-8.5 min-w-[100px]">
              {isLoading ? <Loader2Icon className="animate-spin" /> : "Save"}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AddMultipleStudent;
